import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  FiLogOut, FiCheck, FiX, FiGrid, FiCalendar, FiMail,
  FiPackage, FiBell, FiChevronRight, FiEye, FiUsers,
  FiMessageSquare, FiAlertCircle, FiCheckCircle,
  FiEdit2, FiToggleLeft, FiToggleRight, FiBarChart2,
  FiTrendingUp, FiDollarSign, FiSave, FiXCircle,
  FiFilter, FiSearch, FiRefreshCw, FiChevronDown,
  FiStar, FiActivity, FiClock, FiArrowUp, FiArrowDown,
} from 'react-icons/fi';

const API   = (path) => `/api${path}`;
const auth  = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });
const fmt   = (d) => d ? new Date(d).toLocaleDateString('en-IN',  { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtDT = (d) => d ? new Date(d).toLocaleString('en-IN',     { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—';
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

/* ── Stat Card ── */
function Card({ icon: Icon, label, value, color, bg, sub, trend }) {
  return (
    <div style={{ background:'#fff', borderRadius:16, padding:'20px 22px', boxShadow:'0 1px 14px rgba(0,0,0,0.06)', display:'flex', gap:14, alignItems:'center' }}>
      <div style={{ width:50, height:50, borderRadius:14, background:bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        <Icon size={21} color={color} />
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:'0.7rem', color:'#9ca3af', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:2 }}>{label}</div>
        <div style={{ fontSize:'1.75rem', fontWeight:800, color:'#111827', lineHeight:1.1 }}>{value ?? '—'}</div>
        {sub && <div style={{ fontSize:'0.72rem', color:'#6b7280', marginTop:2 }}>{sub}</div>}
      </div>
      {trend !== undefined && (
        <div style={{ flexShrink:0, display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
          {trend >= 0
            ? <FiArrowUp size={14} color="#16a34a" />
            : <FiArrowDown size={14} color="#dc2626" />}
          <span style={{ fontSize:'0.7rem', fontWeight:700, color: trend >= 0 ? '#16a34a' : '#dc2626' }}>{Math.abs(trend)}%</span>
        </div>
      )}
    </div>
  );
}

/* ── Section Title ── */
function SectionHead({ icon: Icon, title, sub, color = '#1a6b5a' }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
      <div style={{ width:32, height:32, borderRadius:9, background:`${color}18`, display:'flex', alignItems:'center', justifyContent:'center' }}>
        <Icon size={15} color={color} />
      </div>
      <div>
        <div style={{ fontWeight:700, fontSize:'0.93rem', color:'#111827' }}>{title}</div>
        {sub && <div style={{ fontSize:'0.72rem', color:'#9ca3af' }}>{sub}</div>}
      </div>
    </div>
  );
}

/* ── Status Pill ── */
function Pill({ status, size = 'md' }) {
  const map = {
    pending:   { bg:'#fff7ed', color:'#c2410c' },
    confirmed: { bg:'#f0fdf4', color:'#15803d' },
    cancelled: { bg:'#fef2f2', color:'#b91c1c' },
    completed: { bg:'#eff6ff', color:'#1d4ed8' },
  };
  const s = map[status] || map.pending;
  const p = size === 'sm' ? '2px 8px' : '4px 12px';
  return (
    <span style={{ background:s.bg, color:s.color, padding:p, borderRadius:100, fontSize:'0.72rem', fontWeight:700, whiteSpace:'nowrap', textTransform:'capitalize' }}>
      {status}
    </span>
  );
}

/* ── Avatar Initials ── */
function Avatar({ name, size = 34, idx = 0 }) {
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:`hsl(${(idx * 47) % 360},60%,88%)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:size * 0.35, fontWeight:700, color:`hsl(${(idx * 47) % 360},50%,35%)`, flexShrink:0 }}>
      {name?.charAt(0)?.toUpperCase() || '?'}
    </div>
  );
}

/* ── Toggle ── */
function Toggle({ on, onChange, loading }) {
  return (
    <button onClick={onChange} disabled={loading} style={{ background:'none', border:'none', cursor: loading ? 'wait' : 'pointer', padding:0, display:'flex', alignItems:'center', gap:5, opacity: loading ? 0.6 : 1 }}>
      {on ? <FiToggleRight size={26} color="#1a6b5a" /> : <FiToggleLeft size={26} color="#9ca3af" />}
      <span style={{ fontSize:'0.72rem', fontWeight:700, color: on ? '#15803d' : '#9ca3af' }}>{on ? 'Live' : 'Off'}</span>
    </button>
  );
}

/* ── Mini Bar ── */
function MiniBar({ label, value, max, color = '#1a6b5a', sub }) {
  const pct = max > 0 ? Math.min(Math.round((value / max) * 100), 100) : 0;
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.78rem', marginBottom:4 }}>
        <span style={{ color:'#374151', fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'65%' }}>{label}</span>
        <span style={{ color:'#6b7280', fontWeight:700, flexShrink:0 }}>{value}{sub ? ` · ${sub}` : ''}</span>
      </div>
      <div style={{ height:7, background:'#f1f5f9', borderRadius:100 }}>
        <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:100, transition:'width 0.6s' }} />
      </div>
    </div>
  );
}

/* ── Edit Tour Modal ── */
function EditModal({ tour, onClose, onSave }) {
  const [form, setForm] = useState({
    title: tour.title || '', price: tour.price || '',
    discountedPrice: tour.discountedPrice || '', duration: tour.duration || '',
    category: tour.category || 'kashmir', coverImage: tour.coverImage || '',
    isFeatured: tour.isFeatured || false, rating: tour.rating || 4.5,
  });
  const [saving, setSaving] = useState(false);

  const handle = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await onSave(tour._id, { ...form, price: Number(form.price), discountedPrice: form.discountedPrice ? Number(form.discountedPrice) : undefined, rating: Number(form.rating) });
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:540, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.25)' }}>
        <div style={{ padding:'18px 22px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
          <div style={{ fontWeight:700, fontSize:'0.95rem', color:'#111827' }}>Edit Package</div>
          <button onClick={onClose} style={{ background:'#f1f5f9', border:'none', borderRadius:8, padding:'5px 9px', cursor:'pointer', color:'#6b7280' }}><FiXCircle size={17} /></button>
        </div>
        <form onSubmit={handle} style={{ padding:22 }}>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontSize:'0.77rem', fontWeight:600, color:'#374151', marginBottom:5 }}>Package Title</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={iS} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
            <div><label style={lS}>Price (₹)</label><input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} style={iS} /></div>
            <div><label style={lS}>Discounted Price (₹)</label><input type="number" value={form.discountedPrice} onChange={e => setForm(f => ({ ...f, discountedPrice: e.target.value }))} placeholder="No discount" style={iS} /></div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
            <div><label style={lS}>Duration</label><input value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} placeholder="5N/6D" style={iS} /></div>
            <div><label style={lS}>Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={iS}>
                <option value="kashmir">Kashmir</option>
                <option value="ladakh">Ladakh</option>
                <option value="combo">Combo</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={lS}>Rating</label>
            <input type="number" min={0} max={5} step={0.1} value={form.rating} onChange={e => setForm(f => ({ ...f, rating: e.target.value }))} style={iS} />
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={lS}>Cover Image URL</label>
            <input type="url" value={form.coverImage} onChange={e => setForm(f => ({ ...f, coverImage: e.target.value }))} placeholder="https://images.unsplash.com/..." style={iS} />
            {form.coverImage && <div style={{ marginTop:8, borderRadius:8, overflow:'hidden', height:72 }}><img src={form.coverImage} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e => e.target.style.display='none'} /></div>}
          </div>
          <div style={{ marginBottom:20, padding:'12px 14px', background:'#f8fafc', borderRadius:10, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontWeight:600, fontSize:'0.83rem', color:'#374151' }}>Featured Package</span>
            <button type="button" onClick={() => setForm(f => ({ ...f, isFeatured: !f.isFeatured }))} style={{ background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}>
              {form.isFeatured ? <FiToggleRight size={28} color="#1a6b5a" /> : <FiToggleLeft size={28} color="#9ca3af" />}
              <span style={{ fontSize:'0.75rem', fontWeight:700, color: form.isFeatured ? '#15803d' : '#9ca3af' }}>{form.isFeatured ? 'Yes' : 'No'}</span>
            </button>
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <button type="button" onClick={onClose} style={{ flex:1, padding:'10px', border:'1.5px solid #e2e8f0', borderRadius:10, background:'#fff', color:'#6b7280', cursor:'pointer', fontWeight:600, fontSize:'0.83rem' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ flex:2, padding:'10px', border:'none', borderRadius:10, background: saving ? '#9ca3af' : 'linear-gradient(135deg,#1a6b5a,#0d2a22)', color:'#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight:700, fontSize:'0.85rem', display:'flex', alignItems:'center', justifyContent:'center', gap:7 }}>
              <FiSave size={14} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Customer Detail Modal ── */
function CustomerModal({ customer, onClose }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:600, maxHeight:'88vh', overflowY:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.25)' }}>
        <div style={{ padding:'18px 22px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <Avatar name={customer.name} size={40} />
            <div>
              <div style={{ fontWeight:700, color:'#111827' }}>{customer.name}</div>
              <div style={{ fontSize:'0.75rem', color:'#9ca3af' }}>{customer.email} · {customer.phone}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background:'#f1f5f9', border:'none', borderRadius:8, padding:'5px 9px', cursor:'pointer' }}><FiXCircle size={17} color="#6b7280" /></button>
        </div>
        <div style={{ padding:22 }}>
          {/* Summary */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:22 }}>
            {[
              { label:'Total Bookings', value:customer.bookingCount },
              { label:'Total Spent',    value:fmtINR(customer.totalSpent) },
              { label:'Last Booking',   value:fmt(customer.lastBooking) },
            ].map(s => (
              <div key={s.label} style={{ background:'#f8fafc', borderRadius:12, padding:'12px 14px', textAlign:'center' }}>
                <div style={{ fontSize:'1.2rem', fontWeight:800, color:'#111827' }}>{s.value}</div>
                <div style={{ fontSize:'0.7rem', color:'#9ca3af', marginTop:2 }}>{s.label}</div>
              </div>
            ))}
          </div>
          {/* Booking history */}
          <div style={{ fontWeight:700, fontSize:'0.88rem', color:'#374151', marginBottom:10 }}>Booking History</div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {customer.bookings.map((b, i) => (
              <div key={i} style={{ display:'flex', gap:12, alignItems:'center', padding:'11px 14px', background:'#f8fafc', borderRadius:10, border:'1px solid #e2e8f0' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:'0.85rem', color:'#111827' }}>{b.tourTitle}</div>
                  <div style={{ fontSize:'0.72rem', color:'#9ca3af', marginTop:2 }}>Travel: {fmt(b.travelDate)} · Booked: {fmt(b.createdAt)} · {b.adults}A{b.children > 0 ? ` + ${b.children}C` : ''}</div>
                </div>
                <div style={{ textAlign:'right', flexShrink:0 }}>
                  <div style={{ fontWeight:700, color:'#1a6b5a', fontSize:'0.85rem' }}>{fmtINR(b.amount)}</div>
                  <Pill status={b.status} size="sm" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Input styles ─── */
const iS = { width:'100%', padding:'9px 11px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:'0.83rem', boxSizing:'border-box', outline:'none', background:'#fff' };
const lS = { display:'block', fontSize:'0.76rem', fontWeight:600, color:'#374151', marginBottom:5 };

/* ══════════════════════════ SIDEBAR NAV ══════════════════════════ */
function NavItem({ icon: Icon, label, active, onClick, badge }) {
  return (
    <button onClick={onClick} style={{ width:'100%', display:'flex', alignItems:'center', gap:11, padding:'10px 14px', borderRadius:10, border:'none', cursor:'pointer', marginBottom:3, textAlign:'left', transition:'all 0.15s', background: active ? 'rgba(255,255,255,0.15)' : 'transparent', color: active ? '#fff' : 'rgba(255,255,255,0.6)' }}>
      <span style={{ width:32, height:32, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, background: active ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.06)' }}><Icon size={15} /></span>
      <span style={{ fontSize:'0.85rem', fontWeight: active ? 600 : 400, flex:1 }}>{label}</span>
      {badge > 0 && <span style={{ background:'#e07b39', color:'#fff', borderRadius:100, padding:'1px 7px', fontSize:'0.66rem', fontWeight:700 }}>{badge}</span>}
      {active && <FiChevronRight size={13} style={{ opacity:0.5 }} />}
    </button>
  );
}

/* ════════════════════ MAIN DASHBOARD ════════════════════ */
export default function AdminDashboard() {
  const { logout, adminEmail } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab]         = useState('dashboard');
  const [stats, setStats]     = useState(null);
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tours, setTours]     = useState([]);
  const [editTour, setEditTour] = useState(null);
  const [toggling, setToggling] = useState({});

  // Reports
  const [reports, setReports]         = useState(null);
  const [reportLoading, setRLoading]  = useState(false);
  const [reportFilter, setRFilter]    = useState({ status:'', from:'', to:'', search:'', category:'' });

  // Customers
  const [customers, setCustomers]       = useState([]);
  const [custLoading, setCustLoading]   = useState(false);
  const [selectedCust, setSelectedCust] = useState(null);
  const [custSearch, setCustSearch]     = useState('');
  const [reportTab, setReportTab]       = useState('bookings'); // 'bookings' | 'customers'

  const loadTours = useCallback(() => {
    axios.get(API('/tours/admin/all'), auth()).then(r => setTours(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(API('/admin/stats'), auth()).then(r => setStats(r.data)).catch(() => {});
    axios.get(API('/bookings'), auth()).then(r => setBookings(r.data)).catch(() => {});
    axios.get(API('/contacts'), auth()).then(r => setMessages(r.data)).catch(() => {});
    loadTours();
  }, [loadTours]);

  const loadReports = useCallback((filter = reportFilter) => {
    setRLoading(true);
    const params = new URLSearchParams();
    if (filter.status)   params.set('status',   filter.status);
    if (filter.from)     params.set('from',     filter.from);
    if (filter.to)       params.set('to',       filter.to);
    if (filter.search)   params.set('search',   filter.search);
    if (filter.category) params.set('category', filter.category);
    axios.get(API(`/admin/reports?${params}`), auth())
      .then(r => setReports(r.data))
      .catch(() => toast.error('Failed to load reports'))
      .finally(() => setRLoading(false));
  }, [reportFilter]);

  useEffect(() => {
    if (tab === 'reports') { loadReports(); loadCustomers(); }
  // eslint-disable-next-line
  }, [tab]);

  const loadCustomers = () => {
    setCustLoading(true);
    axios.get(API('/admin/customers'), auth())
      .then(r => setCustomers(r.data.customers || []))
      .catch(() => {})
      .finally(() => setCustLoading(false));
  };

  const updateBooking = async (id, status) => {
    try {
      await axios.patch(API(`/bookings/${id}/status`), { status }, auth());
      setBookings(prev => prev.map(b => b._id === id ? { ...b, status } : b));
      toast.success(`Booking ${status}!`);
    } catch { toast.error('Failed to update'); }
  };

  const markRead = async (id) => {
    try {
      await axios.patch(API(`/contacts/${id}/read`), {}, auth());
      setMessages(prev => prev.map(m => m._id === id ? { ...m, isRead: true } : m));
    } catch {}
  };

  const toggleTour = async (id) => {
    setToggling(t => ({ ...t, [id]: true }));
    try {
      const { data } = await axios.patch(API(`/tours/${id}/toggle`), {}, auth());
      setTours(prev => prev.map(t => t._id === id ? { ...t, isActive: data.isActive } : t));
      toast.success(data.isActive ? 'Package is now Live ✅' : 'Package disabled');
    } catch { toast.error('Toggle failed'); }
    finally { setToggling(t => ({ ...t, [id]: false })); }
  };

  const saveTour = async (id, data) => {
    try {
      await axios.put(API(`/tours/${id}`), data, auth());
      loadTours(); toast.success('Package updated! ✅');
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); throw err; }
  };

  const pendingCount = bookings.filter(b => b.status === 'pending').length;
  const unreadCount  = messages.filter(m => !m.isRead).length;
  const initials     = adminEmail ? adminEmail.charAt(0).toUpperCase() : 'A';

  const filteredCusts = customers.filter(c =>
    !custSearch || c.name?.toLowerCase().includes(custSearch.toLowerCase()) ||
    c.email?.toLowerCase().includes(custSearch.toLowerCase()) ||
    c.phone?.includes(custSearch)
  );

  const navItems = [
    { id:'dashboard', icon:FiGrid,      label:'Dashboard',  badge:0 },
    { id:'bookings',  icon:FiCalendar,  label:'Bookings',   badge:pendingCount },
    { id:'messages',  icon:FiMail,      label:'Messages',   badge:unreadCount },
    { id:'tours',     icon:FiPackage,   label:'Packages',   badge:0 },
    { id:'reports',   icon:FiBarChart2, label:'Reports',    badge:0 },
  ];

  return (
    <div style={{ minHeight:'100vh', display:'flex', background:'#f1f5f9', fontFamily:"'Poppins',sans-serif" }}>

      {/* ── SIDEBAR ── */}
      <aside style={{ width:232, background:'linear-gradient(180deg,#0d2a22,#0a2019)', display:'flex', flexDirection:'column', flexShrink:0, position:'sticky', top:0, height:'100vh' }}>
        <div style={{ padding:'20px 18px 18px', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:36, height:36, borderRadius:9, background:'linear-gradient(135deg,#1a6b5a,#e07b39)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem' }}>🏔️</div>
            <div>
              <div style={{ color:'#fff', fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:'0.9rem' }}>Kashmir Magic</div>
              <div style={{ color:'rgba(255,255,255,0.35)', fontSize:'0.62rem', letterSpacing:'0.1em', textTransform:'uppercase' }}>Admin Panel</div>
            </div>
          </div>
        </div>
        <nav style={{ padding:'14px 10px', flex:1 }}>
          <div style={{ fontSize:'0.62rem', color:'rgba(255,255,255,0.28)', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', padding:'0 6px', marginBottom:8 }}>Navigation</div>
          {navItems.map(item => <NavItem key={item.id} {...item} active={tab === item.id} onClick={() => setTab(item.id)} />)}
        </nav>
        <div style={{ padding:'14px 14px 18px', borderTop:'1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:10 }}>
            <div style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg,#1a6b5a,#3aafa9)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.8rem', fontWeight:700, color:'#fff', flexShrink:0 }}>{initials}</div>
            <div style={{ flex:1, overflow:'hidden' }}>
              <div style={{ color:'#fff', fontSize:'0.75rem', fontWeight:600 }}>Administrator</div>
              <div style={{ color:'rgba(255,255,255,0.35)', fontSize:'0.66rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{adminEmail}</div>
            </div>
          </div>
          <button onClick={() => { logout(); navigate('/admin/login'); }} style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:7, padding:'8px', borderRadius:8, border:'1px solid rgba(255,255,255,0.1)', background:'rgba(255,255,255,0.05)', color:'rgba(255,255,255,0.6)', cursor:'pointer', fontSize:'0.78rem' }}>
            <FiLogOut size={13} /> Logout
          </button>
        </div>
      </aside>

      {/* ── MAIN ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>

        {/* Header */}
        <header style={{ background:'#fff', borderBottom:'1px solid #e2e8f0', padding:'0 26px', height:58, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontWeight:700, color:'#111827', fontSize:'0.97rem' }}>
              {{ dashboard:'Dashboard', bookings:'Bookings', messages:'Messages', tours:'Packages', reports:'Reports & Analytics' }[tab]}
            </div>
            <div style={{ fontSize:'0.7rem', color:'#9ca3af' }}>{new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {unreadCount > 0 && (
              <div style={{ position:'relative', cursor:'pointer' }} onClick={() => setTab('messages')}>
                <FiBell size={19} color="#6b7280" />
                <span style={{ position:'absolute', top:-4, right:-5, background:'#e07b39', color:'#fff', borderRadius:'50%', width:15, height:15, fontSize:'0.58rem', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700 }}>{unreadCount}</span>
              </div>
            )}
            <a href="/" target="_blank" rel="noopener noreferrer" style={{ background:'#f1f5f9', border:'1px solid #e2e8f0', color:'#374151', padding:'5px 12px', borderRadius:8, fontSize:'0.75rem', fontWeight:600, textDecoration:'none', display:'flex', alignItems:'center', gap:5 }}>
              <FiEye size={12} /> View Site
            </a>
          </div>
        </header>

        <main style={{ flex:1, overflowY:'auto', padding:24 }}>

          {/* ══════════════ DASHBOARD TAB ══════════════ */}
          {tab === 'dashboard' && (
            <div>
              {/* Pending alert */}
              {pendingCount > 0 && (
                <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:12, padding:'12px 18px', marginBottom:22, display:'flex', alignItems:'center', gap:10 }}>
                  <FiAlertCircle size={16} color="#c2410c" />
                  <span style={{ fontWeight:600, color:'#9a3412', fontSize:'0.85rem', flex:1 }}>{pendingCount} booking{pendingCount > 1 ? 's' : ''} awaiting approval</span>
                  <button onClick={() => setTab('bookings')} style={{ background:'#c2410c', color:'#fff', border:'none', borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:'0.75rem', fontWeight:700 }}>Review Now →</button>
                </div>
              )}

              {/* ── TODAY ── */}
              <SectionHead icon={FiClock} title="Today" sub={new Date().toLocaleDateString('en-IN', { day:'numeric', month:'long', year:'numeric' })} color="#e07b39" />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:28 }}>
                <Card icon={FiCalendar}      label="Today's Bookings"  value={stats?.todayBookings ?? 0}  color="#1d4ed8" bg="#eff6ff" />
                <Card icon={FiDollarSign}    label="Today's Revenue"   value={fmtINR(stats?.todayRevenue)}  color="#15803d" bg="#f0fdf4" />
                <Card icon={FiMessageSquare} label="Today's Enquiries" value={stats?.todayMessages ?? 0}  color="#7c3aed" bg="#faf5ff" />
                <Card icon={FiAlertCircle}   label="Pending Approval"  value={stats?.pendingBookings ?? 0} color="#c2410c" bg="#fff7ed" />
              </div>

              {/* ── THIS MONTH ── */}
              <SectionHead icon={FiCalendar} title="This Month" sub={new Date().toLocaleDateString('en-IN', { month:'long', year:'numeric' })} color="#1d4ed8" />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:28 }}>
                <Card icon={FiCalendar}   label="Monthly Bookings"   value={stats?.monthlyBookings  ?? 0} color="#1d4ed8" bg="#eff6ff" />
                <Card icon={FiDollarSign} label="Monthly Revenue"    value={fmtINR(stats?.monthlyRevenue)} color="#15803d" bg="#f0fdf4" />
                <Card icon={FiCheckCircle} label="Confirmed"         value={stats?.monthlyConfirmed ?? 0} color="#15803d" bg="#f0fdf4" />
                <Card icon={FiXCircle}    label="Cancelled"          value={stats?.monthlyCancelled ?? 0} color="#b91c1c" bg="#fef2f2" />
              </div>

              {/* ── OVERALL ── */}
              <SectionHead icon={FiActivity} title="Overall" sub="All time statistics" color="#7c3aed" />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:28 }}>
                <Card icon={FiDollarSign}    label="Total Revenue"     value={fmtINR(stats?.totalRevenue)}   color="#15803d" bg="#f0fdf4" />
                <Card icon={FiCalendar}      label="Total Bookings"    value={stats?.bookingCount  ?? 0}     color="#1d4ed8" bg="#eff6ff" />
                <Card icon={FiPackage}       label="Active Packages"   value={stats?.tourCount     ?? 0}     color="#1a6b5a" bg="#ecfdf5" sub={`${stats?.totalTours ?? 0} total`} />
                <Card icon={FiMessageSquare} label="Total Enquiries"   value={stats?.contactCount  ?? 0}     color="#7c3aed" bg="#faf5ff" sub={`${stats?.unreadContacts ?? 0} unread`} />
              </div>

              {/* ── Bottom row: recent bookings + top packages ── */}
              <div style={{ display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:20 }}>
                {/* Recent bookings */}
                <div style={{ background:'#fff', borderRadius:16, boxShadow:'0 1px 14px rgba(0,0,0,0.06)', overflow:'hidden' }}>
                  <div style={{ padding:'16px 20px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#111827' }}>Recent Bookings</div>
                    <button onClick={() => setTab('bookings')} style={{ background:'none', border:'none', color:'#1a6b5a', fontSize:'0.75rem', fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:3 }}>View all <FiChevronRight size={13} /></button>
                  </div>
                  <div>
                    {bookings.slice(0, 6).map((b, i) => (
                      <div key={b._id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 20px', borderBottom: i < 5 ? '1px solid #f8fafc' : 'none' }}>
                        <Avatar name={b.name} size={34} idx={i} />
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:600, fontSize:'0.83rem', color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{b.name}</div>
                          <div style={{ fontSize:'0.72rem', color:'#9ca3af', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{b.tourTitle}</div>
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0 }}>
                          <div style={{ fontWeight:700, fontSize:'0.8rem', color:'#1a6b5a' }}>{fmtINR(b.totalAmount)}</div>
                          <Pill status={b.status} size="sm" />
                        </div>
                      </div>
                    ))}
                    {bookings.length === 0 && <div style={{ padding:'32px 20px', textAlign:'center', color:'#9ca3af', fontSize:'0.85rem' }}>No bookings yet</div>}
                  </div>
                </div>

                {/* Right col: quick stats */}
                <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                  {/* Booking status breakdown */}
                  <div style={{ background:'#fff', borderRadius:16, boxShadow:'0 1px 14px rgba(0,0,0,0.06)', padding:'16px 20px' }}>
                    <div style={{ fontWeight:700, fontSize:'0.88rem', color:'#111827', marginBottom:14 }}>Booking Status</div>
                    {[
                      { label:'Confirmed', value: bookings.filter(b=>b.status==='confirmed').length, color:'#16a34a' },
                      { label:'Pending',   value: bookings.filter(b=>b.status==='pending').length,   color:'#ea580c' },
                      { label:'Cancelled', value: bookings.filter(b=>b.status==='cancelled').length, color:'#dc2626' },
                    ].map(s => <MiniBar key={s.label} label={s.label} value={s.value} max={bookings.length || 1} color={s.color} />)}
                  </div>
                  {/* Unread messages */}
                  <div style={{ background:'#fff', borderRadius:16, boxShadow:'0 1px 14px rgba(0,0,0,0.06)', padding:'16px 20px' }}>
                    <div style={{ fontWeight:700, fontSize:'0.88rem', color:'#111827', marginBottom:12 }}>
                      Recent Enquiries {unreadCount > 0 && <span style={{ background:'#e07b39', color:'#fff', borderRadius:100, padding:'1px 8px', fontSize:'0.65rem', marginLeft:6 }}>{unreadCount} new</span>}
                    </div>
                    {messages.slice(0, 3).map((m, i) => (
                      <div key={m._id} style={{ display:'flex', gap:10, alignItems:'flex-start', padding:'9px 0', borderBottom: i < 2 ? '1px solid #f8fafc' : 'none' }}>
                        <Avatar name={m.name} size={30} idx={i + 10} />
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:600, fontSize:'0.8rem', color:'#111827', display:'flex', alignItems:'center', gap:6 }}>
                            {m.name}
                            {!m.isRead && <span style={{ width:7, height:7, borderRadius:'50%', background:'#e07b39', display:'inline-block' }} />}
                          </div>
                          <div style={{ fontSize:'0.7rem', color:'#9ca3af', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{m.message}</div>
                        </div>
                      </div>
                    ))}
                    {messages.length === 0 && <div style={{ fontSize:'0.82rem', color:'#9ca3af', textAlign:'center', padding:'10px 0' }}>No messages yet</div>}
                    <button onClick={() => setTab('messages')} style={{ marginTop:8, width:'100%', border:'1px solid #e2e8f0', background:'#f8fafc', borderRadius:8, padding:'7px', cursor:'pointer', color:'#6b7280', fontSize:'0.75rem', fontWeight:600 }}>View all messages</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════ BOOKINGS TAB ══════════════ */}
          {tab === 'bookings' && (
            <div>
              <div style={{ display:'flex', gap:10, marginBottom:18, flexWrap:'wrap' }}>
                {[
                  { label:'Total',     value:bookings.length,                                 bg:'#f1f5f9', color:'#475569' },
                  { label:'Pending',   value:bookings.filter(b=>b.status==='pending').length,   bg:'#fff7ed', color:'#c2410c' },
                  { label:'Confirmed', value:bookings.filter(b=>b.status==='confirmed').length, bg:'#f0fdf4', color:'#15803d' },
                  { label:'Cancelled', value:bookings.filter(b=>b.status==='cancelled').length, bg:'#fef2f2', color:'#b91c1c' },
                ].map(p => (
                  <div key={p.label} style={{ background:p.bg, color:p.color, padding:'7px 16px', borderRadius:10, fontSize:'0.8rem', fontWeight:700 }}>{p.label}: {p.value}</div>
                ))}
              </div>
              <div style={{ background:'#fff', borderRadius:16, boxShadow:'0 1px 14px rgba(0,0,0,0.06)', overflow:'hidden' }}>
                <div style={{ padding:'16px 22px', borderBottom:'1px solid #f1f5f9', fontWeight:700, fontSize:'0.92rem', color:'#111827' }}>
                  All Bookings <span style={{ color:'#9ca3af', fontWeight:400 }}>({bookings.length})</span>
                </div>
                <BookingsTable bookings={bookings} onUpdate={updateBooking} />
              </div>
            </div>
          )}

          {/* ══════════════ MESSAGES TAB ══════════════ */}
          {tab === 'messages' && (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <div style={{ display:'flex', gap:10, marginBottom:8 }}>
                <div style={{ background:'#faf5ff', color:'#7c3aed', padding:'7px 16px', borderRadius:10, fontSize:'0.8rem', fontWeight:700 }}>Total: {messages.length}</div>
                <div style={{ background:'#fff7ed', color:'#c2410c',  padding:'7px 16px', borderRadius:10, fontSize:'0.8rem', fontWeight:700 }}>Unread: {unreadCount}</div>
              </div>
              {messages.map((m, i) => (
                <div key={m._id} style={{ background:'#fff', borderRadius:14, padding:'18px 22px', boxShadow: m.isRead ? '0 1px 8px rgba(0,0,0,0.04)' : '0 2px 16px rgba(26,107,90,0.1)', border: m.isRead ? '1px solid #e2e8f0' : '1px solid #6ee7b7', display:'flex', gap:14, alignItems:'flex-start' }}>
                  <Avatar name={m.name} size={42} idx={i} />
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginBottom:4 }}>
                      <span style={{ fontWeight:700, color:'#111827', fontSize:'0.88rem' }}>{m.name}</span>
                      {!m.isRead && <span style={{ background:'#059669', color:'#fff', padding:'1px 8px', borderRadius:100, fontSize:'0.63rem', fontWeight:700 }}>NEW</span>}
                      <span style={{ color:'#9ca3af', fontSize:'0.72rem' }}>{fmtDT(m.createdAt)}</span>
                    </div>
                    <div style={{ display:'flex', gap:14, marginBottom:7, flexWrap:'wrap' }}>
                      <span style={{ fontSize:'0.76rem', color:'#6b7280' }}>✉ {m.email}</span>
                      {m.phone && <span style={{ fontSize:'0.76rem', color:'#6b7280' }}>📞 {m.phone}</span>}
                      {m.subject && <span style={{ fontSize:'0.76rem', color:'#1d4ed8', fontWeight:600 }}>📌 {m.subject}</span>}
                    </div>
                    <p style={{ color:'#374151', fontSize:'0.85rem', lineHeight:1.65, margin:0 }}>{m.message}</p>
                  </div>
                  {!m.isRead && (
                    <button onClick={() => markRead(m._id)} style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', color:'#15803d', padding:'6px 12px', borderRadius:8, cursor:'pointer', fontSize:'0.75rem', fontWeight:600, whiteSpace:'nowrap', display:'flex', alignItems:'center', gap:4 }}>
                      <FiCheckCircle size={12} /> Mark Read
                    </button>
                  )}
                </div>
              ))}
              {messages.length === 0 && <div style={{ textAlign:'center', padding:60, color:'#9ca3af', background:'#fff', borderRadius:16 }}>No messages yet</div>}
            </div>
          )}

          {/* ══════════════ PACKAGES TAB ══════════════ */}
          {tab === 'tours' && (
            <div>
              <div style={{ display:'flex', gap:10, marginBottom:18 }}>
                <div style={{ background:'#f0fdf4', color:'#15803d', padding:'7px 16px', borderRadius:10, fontSize:'0.8rem', fontWeight:700 }}>Live: {tours.filter(t=>t.isActive).length}</div>
                <div style={{ background:'#fef2f2', color:'#b91c1c', padding:'7px 16px', borderRadius:10, fontSize:'0.8rem', fontWeight:700 }}>Disabled: {tours.filter(t=>!t.isActive).length}</div>
                <div style={{ background:'#f1f5f9', color:'#475569', padding:'7px 16px', borderRadius:10, fontSize:'0.8rem', fontWeight:700 }}>Total: {tours.length}</div>
              </div>
              <div style={{ background:'#fff', borderRadius:16, boxShadow:'0 1px 14px rgba(0,0,0,0.06)', overflow:'hidden' }}>
                <div style={{ padding:'16px 22px', borderBottom:'1px solid #f1f5f9', fontWeight:700, fontSize:'0.92rem', color:'#111827' }}>All Packages</div>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.83rem' }}>
                    <thead>
                      <tr style={{ background:'#f8fafc', borderBottom:'2px solid #e2e8f0' }}>
                        {['Cover','Title','Category','Duration','Price','Rating','Featured','Status','Actions'].map(h => (
                          <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontWeight:600, color:'#64748b', fontSize:'0.7rem', textTransform:'uppercase', letterSpacing:'0.06em', whiteSpace:'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tours.map((t, i) => (
                        <tr key={t._id} style={{ borderBottom:'1px solid #f1f5f9', opacity: t.isActive ? 1 : 0.55 }}
                          onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                          onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                          <td style={{ padding:'11px 14px' }}>
                            <div style={{ width:48, height:36, borderRadius:7, overflow:'hidden', background:'#f1f5f9' }}>
                              {t.coverImage ? <img src={t.coverImage} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem' }}>🏔️</div>}
                            </div>
                          </td>
                          <td style={{ padding:'11px 14px', maxWidth:180 }}>
                            <div style={{ fontWeight:600, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.title}</div>
                          </td>
                          <td style={{ padding:'11px 14px' }}>
                            <span style={{ background: t.category==='kashmir'?'#f0fdf4':t.category==='ladakh'?'#eff6ff':'#faf5ff', color: t.category==='kashmir'?'#15803d':t.category==='ladakh'?'#1d4ed8':'#7c3aed', padding:'2px 9px', borderRadius:100, fontSize:'0.7rem', fontWeight:600, textTransform:'capitalize' }}>{t.category}</span>
                          </td>
                          <td style={{ padding:'11px 14px', color:'#6b7280', whiteSpace:'nowrap' }}>{t.duration}</td>
                          <td style={{ padding:'11px 14px', whiteSpace:'nowrap' }}>
                            <div style={{ fontWeight:700, color:'#1a6b5a' }}>{fmtINR(t.discountedPrice || t.price)}</div>
                            {t.discountedPrice && t.price && t.discountedPrice < t.price && <div style={{ fontSize:'0.7rem', color:'#9ca3af', textDecoration:'line-through' }}>{fmtINR(t.price)}</div>}
                          </td>
                          <td style={{ padding:'11px 14px', whiteSpace:'nowrap' }}><span style={{ color:'#f59e0b' }}>★</span> <span style={{ fontWeight:600 }}>{t.rating}</span></td>
                          <td style={{ padding:'11px 14px' }}>
                            {t.isFeatured ? <span style={{ background:'#fef9c3', color:'#a16207', padding:'2px 9px', borderRadius:100, fontSize:'0.7rem', fontWeight:700 }}>⭐ Yes</span> : <span style={{ color:'#d1d5db' }}>—</span>}
                          </td>
                          <td style={{ padding:'11px 14px' }}>
                            <Toggle on={t.isActive} onChange={() => toggleTour(t._id)} loading={toggling[t._id]} />
                          </td>
                          <td style={{ padding:'11px 14px' }}>
                            <button onClick={() => setEditTour(t)} style={{ background:'#eff6ff', border:'1px solid #bfdbfe', color:'#1d4ed8', padding:'5px 12px', borderRadius:8, cursor:'pointer', fontSize:'0.75rem', fontWeight:600, display:'flex', alignItems:'center', gap:4 }}>
                              <FiEdit2 size={12} /> Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════ REPORTS TAB ══════════════ */}
          {tab === 'reports' && (
            <div>
              {/* Sub-tabs */}
              <div style={{ display:'flex', gap:8, marginBottom:20 }}>
                {[{ id:'bookings', label:'📋 Booking Reports' }, { id:'customers', label:'👥 Customers' }].map(t => (
                  <button key={t.id} onClick={() => setReportTab(t.id)} style={{ padding:'8px 20px', border:'none', borderRadius:10, cursor:'pointer', fontWeight:600, fontSize:'0.83rem', background: reportTab === t.id ? '#0d2a22' : '#fff', color: reportTab === t.id ? '#fff' : '#6b7280', boxShadow: reportTab === t.id ? '0 2px 10px rgba(13,42,34,0.2)' : '0 1px 4px rgba(0,0,0,0.05)' }}>{t.label}</button>
                ))}
              </div>

              {/* ── BOOKING REPORTS ── */}
              {reportTab === 'bookings' && (
                <div>
                  {/* Summary cards */}
                  {reports && (
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:20 }}>
                      <Card icon={FiCalendar}   label="Filtered Bookings" value={reports.totalFiltered}          color="#1d4ed8" bg="#eff6ff" />
                      <Card icon={FiDollarSign} label="Filtered Revenue"  value={fmtINR(reports.filteredRevenue)} color="#15803d" bg="#f0fdf4" />
                      <Card icon={FiCheckCircle} label="Confirmed"        value={reports.byStatus?.confirmed}    color="#15803d" bg="#f0fdf4" />
                      <Card icon={FiXCircle}    label="Cancelled"         value={reports.byStatus?.cancelled}    color="#b91c1c" bg="#fef2f2" />
                    </div>
                  )}

                  {/* Filters */}
                  <div style={{ background:'#fff', borderRadius:14, padding:'16px 20px', boxShadow:'0 1px 10px rgba(0,0,0,0.05)', marginBottom:18 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
                      <FiFilter size={14} color="#6b7280" />
                      <span style={{ fontWeight:700, fontSize:'0.85rem', color:'#374151' }}>Filters</span>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr auto', gap:10, alignItems:'end' }}>
                      <div>
                        <label style={lS}>Search (name/email/tour)</label>
                        <div style={{ position:'relative' }}>
                          <FiSearch size={13} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:'#9ca3af' }} />
                          <input value={reportFilter.search} onChange={e => setRFilter(f => ({ ...f, search: e.target.value }))} placeholder="Type to search..." style={{ ...iS, paddingLeft:28 }} />
                        </div>
                      </div>
                      <div>
                        <label style={lS}>Status</label>
                        <select value={reportFilter.status} onChange={e => setRFilter(f => ({ ...f, status: e.target.value }))} style={iS}>
                          <option value="">All</option>
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                      <div>
                        <label style={lS}>From Date</label>
                        <input type="date" value={reportFilter.from} onChange={e => setRFilter(f => ({ ...f, from: e.target.value }))} style={iS} />
                      </div>
                      <div>
                        <label style={lS}>To Date</label>
                        <input type="date" value={reportFilter.to} onChange={e => setRFilter(f => ({ ...f, to: e.target.value }))} style={iS} />
                      </div>
                      <div>
                        <label style={lS}>Category</label>
                        <select value={reportFilter.category} onChange={e => setRFilter(f => ({ ...f, category: e.target.value }))} style={iS}>
                          <option value="">All</option>
                          <option value="kashmir">Kashmir</option>
                          <option value="ladakh">Ladakh</option>
                          <option value="combo">Combo</option>
                        </select>
                      </div>
                      <div style={{ display:'flex', gap:6 }}>
                        <button onClick={() => loadReports(reportFilter)} disabled={reportLoading} style={{ padding:'9px 14px', background:'#1a6b5a', border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontWeight:600, fontSize:'0.8rem', display:'flex', alignItems:'center', gap:5, whiteSpace:'nowrap' }}>
                          {reportLoading ? <FiRefreshCw size={13} style={{ animation:'spin 0.8s linear infinite' }} /> : <FiSearch size={13} />} Apply
                        </button>
                        <button onClick={() => { const f = { status:'', from:'', to:'', search:'', category:'' }; setRFilter(f); loadReports(f); }} style={{ padding:'9px 12px', background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:8, color:'#6b7280', cursor:'pointer', fontSize:'0.8rem' }}>
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Bookings Table */}
                  <div style={{ background:'#fff', borderRadius:14, boxShadow:'0 1px 10px rgba(0,0,0,0.05)', overflow:'hidden' }}>
                    <div style={{ padding:'14px 20px', borderBottom:'1px solid #f1f5f9', fontWeight:700, fontSize:'0.88rem', color:'#111827' }}>
                      Results {reports ? `— ${reports.totalFiltered} booking${reports.totalFiltered !== 1 ? 's' : ''} · ${fmtINR(reports.filteredRevenue)} confirmed revenue` : ''}
                    </div>
                    {reportLoading ? (
                      <div style={{ textAlign:'center', padding:40, color:'#9ca3af' }}>Loading...</div>
                    ) : reports?.bookings?.length > 0 ? (
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                          <thead>
                            <tr style={{ background:'#f8fafc', borderBottom:'2px solid #e2e8f0' }}>
                              {['#','Traveller','Email','Phone','Tour Package','Travel Date','Guests','Amount','Status','Booked On'].map(h => (
                                <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontWeight:600, color:'#64748b', fontSize:'0.68rem', textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {reports.bookings.map((b, i) => (
                              <tr key={b._id} style={{ borderBottom:'1px solid #f8fafc' }}
                                onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                                <td style={{ padding:'11px 14px', color:'#9ca3af', fontWeight:600 }}>{i + 1}</td>
                                <td style={{ padding:'11px 14px' }}>
                                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                                    <Avatar name={b.name} size={28} idx={i} />
                                    <span style={{ fontWeight:600, color:'#111827' }}>{b.name}</span>
                                  </div>
                                </td>
                                <td style={{ padding:'11px 14px', color:'#6b7280' }}>{b.email}</td>
                                <td style={{ padding:'11px 14px', color:'#6b7280' }}>{b.phone || '—'}</td>
                                <td style={{ padding:'11px 14px', maxWidth:180 }}>
                                  <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'#374151', fontWeight:500 }}>{b.tourTitle}</div>
                                </td>
                                <td style={{ padding:'11px 14px', color:'#6b7280', whiteSpace:'nowrap' }}>{fmt(b.travelDate)}</td>
                                <td style={{ padding:'11px 14px', whiteSpace:'nowrap' }}>
                                  <span style={{ background:'#f0fdf4', color:'#15803d', padding:'2px 8px', borderRadius:6, fontSize:'0.72rem', fontWeight:600 }}>{b.adults}A{b.children > 0 ? ` +${b.children}C` : ''}</span>
                                </td>
                                <td style={{ padding:'11px 14px', fontWeight:700, color:'#1a6b5a', whiteSpace:'nowrap' }}>{fmtINR(b.totalAmount)}</td>
                                <td style={{ padding:'11px 14px' }}><Pill status={b.status} size="sm" /></td>
                                <td style={{ padding:'11px 14px', color:'#9ca3af', whiteSpace:'nowrap', fontSize:'0.72rem' }}>{fmtDT(b.createdAt)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign:'center', padding:48, color:'#9ca3af' }}>
                        <FiCalendar size={32} style={{ opacity:0.25, marginBottom:10, display:'block', margin:'0 auto 10px' }} />
                        <p style={{ fontSize:'0.85rem' }}>No bookings match your filters</p>
                      </div>
                    )}
                  </div>

                  {/* Monthly Trend */}
                  {reports?.monthlyTrend && (
                    <div style={{ background:'#fff', borderRadius:14, boxShadow:'0 1px 10px rgba(0,0,0,0.05)', padding:'20px', marginTop:18 }}>
                      <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#111827', marginBottom:18 }}>6-Month Booking Trend</div>
                      <div style={{ display:'flex', alignItems:'flex-end', gap:10, height:110 }}>
                        {reports.monthlyTrend.map((m, i) => {
                          const maxC = Math.max(...reports.monthlyTrend.map(x => x.count), 1);
                          const h = Math.max((m.count / maxC) * 90, 4);
                          const isLast = i === reports.monthlyTrend.length - 1;
                          return (
                            <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:5 }}>
                              <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#1a6b5a' }}>{m.count > 0 ? m.count : ''}</div>
                              <div title={`${m.label}: ${m.count} bookings · ${fmtINR(m.revenue)}`}
                                style={{ width:'100%', background: isLast ? '#1a6b5a' : '#bbf7d0', borderRadius:'5px 5px 0 0', height:`${h}px`, transition:'height 0.5s' }} />
                              <div style={{ fontSize:'0.68rem', color:'#9ca3af' }}>{m.label}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── CUSTOMERS ── */}
              {reportTab === 'customers' && (
                <div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:20 }}>
                    <Card icon={FiUsers}      label="Total Customers"   value={customers.length}                                                              color="#1d4ed8" bg="#eff6ff" />
                    <Card icon={FiDollarSign} label="Total Revenue"     value={fmtINR(customers.reduce((s,c) => s + c.totalSpent, 0))}                         color="#15803d" bg="#f0fdf4" />
                    <Card icon={FiCalendar}   label="Avg Bookings/Cust" value={(customers.length ? (customers.reduce((s,c) => s + c.bookingCount, 0) / customers.length).toFixed(1) : 0)} color="#7c3aed" bg="#faf5ff" />
                  </div>

                  {/* Search */}
                  <div style={{ background:'#fff', borderRadius:12, padding:'14px 18px', marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
                    <FiSearch size={14} color="#9ca3af" />
                    <input value={custSearch} onChange={e => setCustSearch(e.target.value)} placeholder="Search by name, email or phone..." style={{ border:'none', outline:'none', flex:1, fontSize:'0.85rem', color:'#374151' }} />
                    {custSearch && <button onClick={() => setCustSearch('')} style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af' }}><FiXCircle size={15} /></button>}
                  </div>

                  {/* Customers table */}
                  <div style={{ background:'#fff', borderRadius:14, boxShadow:'0 1px 10px rgba(0,0,0,0.05)', overflow:'hidden' }}>
                    <div style={{ padding:'14px 20px', borderBottom:'1px solid #f1f5f9', fontWeight:700, fontSize:'0.88rem', color:'#111827' }}>
                      Customer Directory — {filteredCusts.length} traveller{filteredCusts.length !== 1 ? 's' : ''}
                    </div>
                    {custLoading ? (
                      <div style={{ textAlign:'center', padding:40, color:'#9ca3af' }}>Loading customers...</div>
                    ) : filteredCusts.length > 0 ? (
                      <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                          <thead>
                            <tr style={{ background:'#f8fafc', borderBottom:'2px solid #e2e8f0' }}>
                              {['#','Customer','Contact','Bookings','Total Spent','First Booking','Last Booking','Action'].map(h => (
                                <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontWeight:600, color:'#64748b', fontSize:'0.68rem', textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {filteredCusts.map((c, i) => (
                              <tr key={c.email} style={{ borderBottom:'1px solid #f8fafc', cursor:'pointer' }}
                                onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                                <td style={{ padding:'12px 14px', color:'#9ca3af', fontWeight:600 }}>{i + 1}</td>
                                <td style={{ padding:'12px 14px' }}>
                                  <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                                    <Avatar name={c.name} size={34} idx={i} />
                                    <span style={{ fontWeight:700, color:'#111827' }}>{c.name}</span>
                                  </div>
                                </td>
                                <td style={{ padding:'12px 14px' }}>
                                  <div style={{ color:'#374151', fontSize:'0.8rem' }}>{c.email}</div>
                                  <div style={{ color:'#9ca3af', fontSize:'0.72rem' }}>{c.phone}</div>
                                </td>
                                <td style={{ padding:'12px 14px' }}>
                                  <span style={{ background:'#eff6ff', color:'#1d4ed8', padding:'3px 10px', borderRadius:100, fontWeight:700, fontSize:'0.75rem' }}>{c.bookingCount}</span>
                                </td>
                                <td style={{ padding:'12px 14px', fontWeight:700, color:'#1a6b5a' }}>{fmtINR(c.totalSpent)}</td>
                                <td style={{ padding:'12px 14px', color:'#9ca3af', whiteSpace:'nowrap', fontSize:'0.75rem' }}>{fmt(c.firstBooking)}</td>
                                <td style={{ padding:'12px 14px', color:'#9ca3af', whiteSpace:'nowrap', fontSize:'0.75rem' }}>{fmt(c.lastBooking)}</td>
                                <td style={{ padding:'12px 14px' }}>
                                  <button onClick={() => setSelectedCust(c)} style={{ background:'#eff6ff', border:'1px solid #bfdbfe', color:'#1d4ed8', padding:'5px 12px', borderRadius:8, cursor:'pointer', fontSize:'0.75rem', fontWeight:600, display:'flex', alignItems:'center', gap:4 }}>
                                    <FiEye size={12} /> View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign:'center', padding:48, color:'#9ca3af' }}>
                        <FiUsers size={32} style={{ opacity:0.2, marginBottom:10, display:'block', margin:'0 auto 10px' }} />
                        <p style={{ fontSize:'0.85rem' }}>No customers found</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

        </main>
      </div>

      {/* Modals */}
      {editTour    && <EditModal      tour={editTour}        onClose={() => setEditTour(null)}    onSave={saveTour} />}
      {selectedCust && <CustomerModal customer={selectedCust} onClose={() => setSelectedCust(null)} />}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ── Bookings Table (reusable) ── */
function BookingsTable({ bookings, onUpdate }) {
  if (!bookings.length) return <div style={{ textAlign:'center', padding:'48px 20px', color:'#9ca3af', fontSize:'0.85rem' }}>No bookings yet</div>;
  return (
    <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
        <thead>
          <tr style={{ background:'#f8fafc', borderBottom:'2px solid #e2e8f0' }}>
            {['Traveller','Tour Package','Travel Date','Guests','Amount','Status','Actions'].map(h => (
              <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontWeight:600, color:'#64748b', fontSize:'0.7rem', textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bookings.map((b, i) => (
            <tr key={b._id} style={{ borderBottom:'1px solid #f1f5f9' }}
              onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              <td style={{ padding:'13px 14px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                  <Avatar name={b.name} size={32} idx={i} />
                  <div>
                    <div style={{ fontWeight:600, color:'#111827' }}>{b.name}</div>
                    <div style={{ fontSize:'0.7rem', color:'#9ca3af' }}>{b.email}</div>
                    {b.phone && <div style={{ fontSize:'0.68rem', color:'#9ca3af' }}>{b.phone}</div>}
                  </div>
                </div>
              </td>
              <td style={{ padding:'13px 14px', color:'#374151', maxWidth:180 }}>
                <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontWeight:500 }}>{b.tourTitle || 'N/A'}</div>
              </td>
              <td style={{ padding:'13px 14px', color:'#6b7280', whiteSpace:'nowrap' }}>{fmt(b.travelDate)}</td>
              <td style={{ padding:'13px 14px' }}>
                <span style={{ background:'#f0fdf4', color:'#15803d', padding:'2px 8px', borderRadius:6, fontSize:'0.72rem', fontWeight:600 }}>
                  {b.adults}A {b.children > 0 ? `+${b.children}C` : ''}
                </span>
              </td>
              <td style={{ padding:'13px 14px', fontWeight:700, color:'#1a6b5a', whiteSpace:'nowrap' }}>{fmtINR(b.totalAmount)}</td>
              <td style={{ padding:'13px 14px' }}><Pill status={b.status} /></td>
              <td style={{ padding:'13px 14px' }}>
                {b.status === 'pending' ? (
                  <div style={{ display:'flex', gap:5 }}>
                    <button onClick={() => onUpdate(b._id, 'confirmed')} style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', color:'#15803d', padding:'4px 10px', borderRadius:7, cursor:'pointer', fontSize:'0.72rem', fontWeight:600, display:'flex', alignItems:'center', gap:3 }}>
                      <FiCheck size={11} /> Confirm
                    </button>
                    <button onClick={() => onUpdate(b._id, 'cancelled')} style={{ background:'#fef2f2', border:'1px solid #fecaca', color:'#b91c1c', padding:'4px 10px', borderRadius:7, cursor:'pointer', fontSize:'0.72rem', fontWeight:600, display:'flex', alignItems:'center', gap:3 }}>
                      <FiX size={11} /> Cancel
                    </button>
                  </div>
                ) : <span style={{ fontSize:'0.75rem', color:'#9ca3af' }}>—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
